<template>
  <div
    class="webhook-options-modal"
    :class="{ absolute: !ignorePlacement }"
    data-cy="webhooks-menu"
    :style="style"
    @click.stop.prevent
  >
    <div v-if="isGlobal && dealName" class="px-4 pt-2">({{ dealName }})</div>
    <div
      class="border-b border-gray-100"
      :class="isGlobal ? 'px-4 pt-2 pb-4' : 'p-4'"
    >
      <search-input
        v-model="search"
        no-margin
        no-padding
        :disabled="loading"
        @click.stop="
          (event: Event) => (event.target as HTMLInputElement)?.focus()
        "
        @update:model-value="$emit('search:update', $event)"
      />
    </div>
    <div>
      <loader :is-loading="isGlobal ? triggering || globalLoading : loading" />
      <ul class="max-h-[305px] overflow-auto p-2" @scroll="handleScroll">
        <custom-webhook-item
          v-for="webhook in filteredWebhooks"
          :key="webhook.id"
          :webhook="webhook"
          :show-tooltip="showTooltip"
          @webhook:trigger="handleWebhookTrigger"
        />
        <li
          v-if="!filteredWebhooks?.length"
          data-cy="webhooks-no-results"
          class="text-center"
        >
          {{ $t("COMMON.NO_RESULTS") }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";

import { usePromiseWrapper } from "@/hooks/common";
import applicationsApiService from "@/services/modules/applications";

import CustomWebhookItem from "@/components/communication/CustomWebhookItem.vue";
import Loader from "@/components/ui/Loader.vue";
import SearchInput from "@/components/ui/inputs/SearchInput.vue";

import type {
  CustomWebhook,
  CustomWebhookTriggerPayload,
} from "@/models/customWebhook";

const emit = defineEmits<{
  "search:update": [string];
  "webhook:trigger": [CustomWebhookTriggerPayload];
}>();

const {
  items,
  appId,
  style,
  isGlobal,
  triggering = false,
  globalLoading = false,
  ignorePlacement = false,
} = defineProps<{
  items: Partial<CustomWebhook>[];
  appId: string;
  style?: Record<string, string | undefined>;
  isGlobal?: boolean;
  dealName?: string;
  triggering?: boolean;
  globalLoading?: boolean;
  ignorePlacement?: boolean;
}>();

const search = ref("");
const showTooltip = ref(true);

const filteredWebhooks = computed(() => {
  if (!search.value || isGlobal) {
    return items;
  }
  return items.filter((item) =>
    item.name?.toLowerCase().includes(search.value.toLowerCase())
  );
});

const { fetchWrapper: triggerCustomWebhook, loading } = usePromiseWrapper(
  async (webhook: CustomWebhookTriggerPayload) => {
    if (!appId || !webhook.id) {
      return;
    }
    await applicationsApiService.triggerCustomWebhook(appId, {
      custom_webhook_id: webhook.id as string,
      parameters: webhook.parameters,
    });
  }
);

const handleWebhookTrigger = (webhook: CustomWebhookTriggerPayload) =>
  isGlobal ? emit("webhook:trigger", webhook) : triggerCustomWebhook(webhook);

const handleScroll = () => {
  // Hide tooltip when scrolling
  showTooltip.value = false;
  showTooltip.value = true;
};
</script>

<style scoped>
@reference "@/assets/tailwind.css";

.webhook-options-modal {
  @apply z-100 max-h-100 min-h-30 max-w-145 min-w-80 rounded bg-white shadow-lg transition-all;
}
</style>
